import React, { useState, useEffect } from "react";
import { Card, InputNumber, Button, Space, Alert, Divider } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { updateBaseFeesModel } from "../../store/slice/pricing";
import { PricingPreviewTable } from "./PricingCalculatorPreview";

const LOCALITIES = [
  { key: "metro", label: "Metro (5M+ population)", color: "#ff4d4f" },
  { key: "urban", label: "Urban (1M-5M population)", color: "#1890ff" },
  { key: "suburban", label: "Suburban (100K-1M population)", color: "#722ed1" },
  { key: "rural", label: "Rural (<100K population)", color: "#52c41a" },
];

function BaseFeesPanel() {
  const dispatch = useDispatch();
  const {
    baseFeesModel,
    demandMultipliers,
    isLoading,
    message,
  } = useSelector((state) => state.pricing);

  const [fees, setFees] = useState(baseFeesModel);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [changeWarnings, setChangeWarnings] = useState([]);

  useEffect(() => {
    setFees(baseFeesModel);
  }, [baseFeesModel]);

  const handleFeeChange = (locality, value) => {
    const oldValue = fees[locality];
    setFees((prev) => ({
      ...prev,
      [locality]: value || 0,
    }));

    // Check for large changes (>50%)
    if (oldValue > 0 && value) {
      const percentChange = Math.abs((value - oldValue) / oldValue) * 100;
      if (percentChange > 50) {
        if (!changeWarnings.includes(locality)) {
          setChangeWarnings((prev) => [...prev, locality]);
        }
      } else {
        setChangeWarnings((prev) => prev.filter((l) => l !== locality));
      }
    }
  };

  const handleSave = async () => {
    // Validation
    const hasZero = Object.values(fees).some((fee) => fee <= 0);
    if (hasZero) {
      // Show error is handled by Redux
      return;
    }

    setIsSubmitting(true);
    try {
      const resultAction = await dispatch(updateBaseFeesModel(fees));
      if (updateBaseFeesModel.fulfilled.match(resultAction)) {
        setChangeWarnings([]);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFees(baseFeesModel);
    setChangeWarnings([]);
  };

  return (
    <Card
      title="Base Fees by Locality"
      className="mb-6"
      extra={
        <span className="text-xs text-gray-600">
          Set base fee for each locality. Multipliers will be applied automatically.
        </span>
      }
    >
      <Alert
        type="info"
        showIcon
        message="Base Fee × Demand Multiplier = Final Price"
        description={`High: ×${demandMultipliers.high}, Standard: ×${demandMultipliers.standard}, Low: ×${demandMultipliers.low}`}
        className="mb-6"
      />

      {message && (
        <Alert
          type="success"
          message="Updated Successfully"
          description={message}
          closable
          className="mb-6"
        />
      )}

      <Space direction="vertical" className="w-full" size="large">
        {/* Input Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {LOCALITIES.map((locality) => (
            <div
              key={locality.key}
              className="p-4 border-l-4 rounded bg-gray-50"
              style={{ borderLeftColor: locality.color }}
            >
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {locality.label}
              </label>
              <div className="flex items-center gap-2">
                <span className="text-gray-600">₹</span>
                <InputNumber
                  value={fees[locality.key]}
                  onChange={(value) => handleFeeChange(locality.key, value)}
                  className="flex-1"
                  min={1}
                  step={10}
                  precision={0}
                  disabled={isLoading || isSubmitting}
                />
              </div>

              {changeWarnings.includes(locality.key) && (
                <div className="mt-2 p-2 bg-yellow-100 border border-yellow-300 rounded text-xs text-yellow-800">
                  ⚠️ Large change detected (>50%)
                </div>
              )}

              {/* Quick preview */}
              <div className="mt-3 p-2 bg-white rounded border border-gray-200 text-xs">
                <p className="text-gray-700 font-medium mb-1">Calculated Prices:</p>
                <p className="text-gray-600">
                  High: ₹{Math.round(fees[locality.key] * demandMultipliers.high)} |
                  Standard: ₹{Math.round(fees[locality.key] * demandMultipliers.standard)} |
                  Low: ₹{Math.round(fees[locality.key] * demandMultipliers.low)}
                </p>
              </div>
            </div>
          ))}
        </div>

        <Divider />

        {/* Live Preview Matrix */}
        <div>
          <h4 className="font-semibold mb-3">Live Preview - All Prices</h4>
          <p className="text-sm text-gray-600 mb-3">
            This shows how your base fees will be calculated across all demand tiers:
          </p>
          <PricingPreviewTable
            model="baseMultiplier"
            priceType="joiningFee"
            baseFeesModel={fees}
            demandMultipliers={demandMultipliers}
            directValues={{}}
            overrides={[]}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 justify-end">
          <Button onClick={handleReset} disabled={isSubmitting}>
            Reset
          </Button>
          <Button
            type="primary"
            onClick={handleSave}
            loading={isSubmitting}
            disabled={Object.values(fees).some((f) => f <= 0) || isLoading}
            className="bg-orange-500 hover:bg-orange-600"
          >
            {isSubmitting ? "Saving..." : "Save Base Fees"}
          </Button>
        </div>
      </Space>
    </Card>
  );
}

export default BaseFeesPanel;
